import * as React from 'react';
import { Group, GroupResponseWrapper } from '@wix/social-groups-api';
import { ApiTypes, GroupAppsMap } from '@wix/social-groups-api/dist/src/types';
import { Container, SettingsLayout } from '@wix/social-groups-common';
import { compose } from '@wix/social-groups-common/dist/src/compose';
import {
  WithGroup,
  WithGroupProps,
  withUploadAction,
  WithUploadProps,
} from '../Context';
import { GroupUpdate, withGroupUpdate } from '../Context/GroupUpdate';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { DetailsSettings } from './DetailsSettings';
import { TabsSettings } from './TabsSettings';
import styles from './GroupSettings.st.css';
import { UpdateProgress } from '@wix/social-groups-common/dist/src/components/ContentEditor/UpdateProgress';
import {
  BIUserEntry,
  tryToCallBi,
  withBiLogger,
  WithBiLoggerProps,
} from '@wix/social-groups-common/dist/src/context';
import { GroupPermissions } from './GroupPermissions';
import { GroupApps, withGroupApps } from '../Context/GroupApps';
import {
  withTpaComponentsConfig,
  WithTpaComponentsConfigProps,
} from '../Context/withTpaComponentsConfig';
import {
  withExperiments,
  InjectedExperimentsProps,
} from '@wix/wix-experiments-react';

interface GroupSettingsProps {
  onCancel();
}

interface GroupSettingsComponentProps
  extends GroupSettingsProps,
    WithGroupProps,
    GroupApps,
    GroupUpdate,
    WithUploadProps,
    WithBiLoggerProps,
    WithTpaComponentsConfigProps,
    InjectedExperimentsProps,
    InjectedTranslateProps {}

interface GroupSettingsComponentState {
  group: ApiTypes.v1.GroupResponse;
  apps: GroupAppsMap;
  imageUploading: boolean;
  activeTabIndex: number;
  editedTabs: { [key in SettingsTabs]: boolean };
}

const enum SettingsTabs {
  DETAILS,
  TABS,
  PERMISSIONS,
}

class GroupSettingsComponent extends React.Component<
  GroupSettingsComponentProps,
  GroupSettingsComponentState
> {
  static displayName = 'GroupSettingsComponent';
  private groupWrapper: GroupResponseWrapper;
  private uploadFileName: string;

  constructor(props: Readonly<GroupSettingsComponentProps>) {
    super(props);
    this.state = {
      apps: props.apps,
      group: props.group,
      imageUploading: false,
      activeTabIndex: 0,
      editedTabs: {} as any,
    };
    this.groupWrapper = Group.getGroupResponseWrapper(this.state.group);
  }

  componentDidUpdate(
    prevProps: Readonly<GroupSettingsComponentProps>,
    prevState: Readonly<GroupSettingsComponentState>,
  ): void {
    const { group, apps } = this.props;
    if (this.props.updateProgress === UpdateProgress.UPDATING) {
      return;
    }
    if (prevProps.group !== group) {
      this.groupWrapper = Group.getGroupResponseWrapper(group);
      this.setState({ group });
    }
    if (prevProps.apps !== apps) {
      this.setState({ apps });
    }
    if (prevProps.uploadedRegistry !== this.props.uploadedRegistry) {
      this.updateGroupLogo();
    }
    if (
      prevProps.updateProgress === UpdateProgress.UPDATING &&
      this.props.updateProgress === UpdateProgress.STALE
    ) {
      if (this.state.imageUploading) {
        // image upload success
        this.setState({ imageUploading: false });
      } else {
        // group update success
        this.props.onCancel();
      }
    }
  }

  render() {
    const { t, onCancel, updateProgress, mobile } = this.props;
    const { activeTabIndex } = this.state;
    const title = t('groups-web.settings');
    const cancel = t('groups-web.cancel');
    const save = t('groups-web.save');
    return (
      <SettingsLayout
        mobile={mobile}
        title={title}
        tabs={this.getItems()}
        activeTabIndex={activeTabIndex}
        cancelLabel={cancel}
        saveLabel={save}
        onTabClick={this.onTabClick}
        onOk={this.handleClickOnSave}
        onCancel={onCancel}
        okDisabled={this.canUpdateGroup()}
        updating={updateProgress === UpdateProgress.UPDATING}
      >
        <Container {...styles('root', {}, this.props)}>
          {this.renderActiveTab()}
        </Container>
      </SettingsLayout>
    );
  }

  private canUpdateGroup() {
    const { updateProgress } = this.props;
    return (
      !this.groupWrapper.getTitle() ||
      updateProgress === UpdateProgress.UPDATING
    );
  }

  private updateGroupLogo() {
    const uploadFile = this.props.uploadedRegistry.find(
      file => file.filename === this.uploadFileName,
    );
    if (uploadFile) {
      const group = this.groupWrapper.setLogoUrl(uploadFile.uploadResponse[0]);
      const { editedTabs } = this.state;
      editedTabs[SettingsTabs.DETAILS] = true;
      this.setState({ group, imageUploading: false, editedTabs });
    }
  }

  private readonly onLogoUpload = (image: File) => {
    const { name } = image;
    this.props.uploadFiles(name, image);
    this.uploadFileName = name;
    const { editedTabs } = this.state;
    editedTabs[SettingsTabs.DETAILS] = true;
    this.setState({ imageUploading: true, editedTabs });
  };

  private readonly onPrivacyChange = (
    privacyLevel: ApiTypes.v1.GroupPrivacyLevel,
  ) => {
    const group = this.groupWrapper.setPrivacy(privacyLevel);
    const { editedTabs } = this.state;
    editedTabs[SettingsTabs.DETAILS] = true;
    this.setState({ group, editedTabs });
  };

  private readonly onTitleChange = (title: string) => {
    const group = this.groupWrapper.setTitle(title);
    const { editedTabs } = this.state;
    editedTabs[SettingsTabs.DETAILS] = true;
    this.setState({ group, editedTabs });
  };

  private readonly onDisplaySwitch = () => {
    //TODO: update evid:10 too
  };

  private readonly onGroupPermissionChange = (
    permission: ApiTypes.v1.RoleInGroup,
  ) => {
    const group = this.groupWrapper.setMemberPermission(permission);
    const { editedTabs } = this.state;
    editedTabs[SettingsTabs.PERMISSIONS] = true;
    this.setState({ group, editedTabs });
  };

  private readonly getItems = () => {
    const { t, experiments } = this.props;
    const details = t('groups-web.settings.details');
    const tabs = t('groups-web.settings.tabs');
    const permissions = t('groups-web.settings.permissions');
    if (experiments.enabled('specs.groups.TabsAndPermissionsInLiveSettings')) {
      return [
        {
          title: details,
        },
        {
          title: tabs,
        },
        {
          title: permissions,
        },
      ];
    }
    return [
      {
        title: details,
      },
    ];
  };

  private readonly onTabClick = selectedTabIndex => {
    this.setState({
      activeTabIndex: selectedTabIndex,
    });
  };

  private renderActiveTab() {
    const { activeTabIndex } = this.state;

    switch (activeTabIndex) {
      case SettingsTabs.DETAILS:
        return (
          <DetailsSettings
            title={this.groupWrapper.getTitle()}
            privacyLevel={this.groupWrapper.getPrivacy()}
            logoUrl={this.groupWrapper.getLogoUrl()}
            onDisplaySwitch={this.onDisplaySwitch}
            onLogoUpload={this.onLogoUpload}
            onPrivacyChange={this.onPrivacyChange}
            onTitleChange={this.onTitleChange}
          />
        );
      case SettingsTabs.TABS:
        return (
          <TabsSettings
            tabSettings={this.appsToTabSettings()}
            updateTab={this.updateGroupApp}
          />
        );
      case SettingsTabs.PERMISSIONS:
        return (
          <GroupPermissions
            memberPermission={this.groupWrapper.getMemberPermission()}
            onChange={this.onGroupPermissionChange}
          />
        );
      default:
        return null;
    }
  }

  private readonly updateGroupApp = (groupApp: ApiTypes.v1.GroupApp) => {
    const { apps, editedTabs } = this.state;
    editedTabs[SettingsTabs.TABS] = true;
    this.setState({
      editedTabs,
      apps: {
        ...apps,
        [groupApp.key]: {
          ...apps[groupApp.key],
          ...groupApp,
        },
      },
    });
  };

  private readonly appsToTabSettings = () => {
    const { apps } = this.state;
    if (!apps) {
      return [];
    }
    return [
      ApiTypes.v1.GroupAppKey.FEED_APP,
      ApiTypes.v1.GroupAppKey.GALLERY_APP,
      ApiTypes.v1.GroupAppKey.MEMBERS_APP,
      ApiTypes.v1.GroupAppKey.ABOUT_APP,
    ].map(appKey => {
      return apps[appKey];
    });
  };

  private readonly handleClickOnSave = () => {
    const { editedTabs } = this.state;
    this.sendBi();

    if (editedTabs[SettingsTabs.TABS]) {
      this.props.configureApps(Object.values(this.state.apps));
    }
    if (
      editedTabs[SettingsTabs.PERMISSIONS] ||
      editedTabs[SettingsTabs.DETAILS]
    ) {
      this.updateGroup();
    }
  };

  private updateGroup() {
    try {
      const paths = this.groupWrapper.getUpdatePaths();
      const { details, settings } = this.state.group;
      this.props.updateGroup(paths, details, settings);
    } catch (e) {
      console.error('Update Group: FAIL');
    }
  }

  private sendBi() {
    const { biLogger, group } = this.props;
    const { apps, editedTabs } = this.state;

    const commonBiParams = {
      userEntry: BIUserEntry.SITE,
      group_id: group.groupId,
      origin: 'save_button',
    };

    Object.keys(editedTabs).forEach(editedTab => {
      switch (editedTab) {
        case SettingsTabs.TABS.toString():
          tryToCallBi(async () => {
            await biLogger.groupSettingsSave({
              ...commonBiParams,
              tabName: 'tabs',
              show_media: apps[ApiTypes.v1.GroupAppKey.GALLERY_APP].installed,
              show_members: apps[ApiTypes.v1.GroupAppKey.MEMBERS_APP].installed,
              edit_tab: JSON.stringify({
                discussion: apps[ApiTypes.v1.GroupAppKey.FEED_APP].customName,
                media: apps[ApiTypes.v1.GroupAppKey.GALLERY_APP].customName,
                members: apps[ApiTypes.v1.GroupAppKey.MEMBERS_APP].customName,
                about: apps[ApiTypes.v1.GroupAppKey.ABOUT_APP].customName,
              }),
            });
          });
          return;
        case SettingsTabs.PERMISSIONS.toString():
          tryToCallBi(async () => {
            await biLogger.groupSettingsSave({
              ...commonBiParams,
              tabName: 'permissions',
              member_toggle:
                this.groupWrapper.getMemberPermission() ===
                ApiTypes.v1.RoleInGroup.GROUP_MEMBER,
            });
          });
          return;
        case SettingsTabs.DETAILS.toString():
          tryToCallBi(async () => {
            await biLogger.groupSettingsSave({
              ...commonBiParams,
              tabName: 'details',
              type: this.groupWrapper.getPrivacy(),
              withPhoto: !!this.groupWrapper.getLogoUrl(),
              display_cover: true, // currently we have no way to change it
            });
          });
          return;
        default:
          return;
      }
    });
  }
}

const enhance = compose(
  translate(),
  WithGroup,
  withGroupApps,
  withGroupUpdate,
  withUploadAction,
  withTpaComponentsConfig,
  withExperiments,
  withBiLogger,
);
export const EditGroupSettings = enhance(
  GroupSettingsComponent,
) as React.ComponentType<GroupSettingsProps>;
